import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const VIEW_MODES = {
  SIMPLE: 'simple',      // CEO view - big numbers, minimal details
  STANDARD: 'standard',  // Finance view - balanced dashboard
  DETAILED: 'detailed'   // Accountant view - all details, tables, journal entries
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewModeState] = useState(VIEW_MODES.STANDARD);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setCompany(response.data.company);
      
      // Load view mode from preferences
      const preferences = response.data.user?.preferences || {};
      if (preferences.view_mode && Object.values(VIEW_MODES).includes(preferences.view_mode)) {
        setViewModeState(preferences.view_mode);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  // Refresh company data (used after settings update)
  const refreshCompany = async () => {
    try {
      const response = await api.get('/auth/me');
      setCompany(response.data.company);
    } catch (error) {
      console.error('Failed to refresh company data:', error);
    }
  };

  // Update view mode and persist to backend
  const setViewMode = useCallback(async (mode) => {
    if (!Object.values(VIEW_MODES).includes(mode)) {
      console.error('Invalid view mode:', mode);
      return;
    }
    
    setViewModeState(mode);
    
    // Persist to backend
    try {
      await api.put('/users/me/preferences', { view_mode: mode });
    } catch (error) {
      console.error('Failed to save view mode preference:', error);
    }
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData, company: companyData } = response.data;
    
    localStorage.setItem('token', access_token);
    setUser(userData);
    setCompany(companyData);
    
    // Load view mode from user preferences
    const preferences = userData?.preferences || {};
    if (preferences.view_mode && Object.values(VIEW_MODES).includes(preferences.view_mode)) {
      setViewModeState(preferences.view_mode);
    }
    
    return response.data;
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    
    // Check if registration is pending approval
    if (response.data.pending) {
      return response.data;
    }
    
    // Normal flow for auto-approved users (platform admin)
    const { access_token, user: userData, company: companyData } = response.data;
    
    localStorage.setItem('token', access_token);
    setUser(userData);
    setCompany(companyData);
    setViewModeState(VIEW_MODES.STANDARD); // Default for new users
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCompany(null);
    setViewModeState(VIEW_MODES.STANDARD);
  };

  // Get company's default currency
  const currency = company?.currency || 'USD';

  // Role helpers
  const isAdmin = user?.role === 'admin';
  const isFinance = user?.role === 'finance';
  const isAccountant = user?.role === 'accountant';
  const isViewer = user?.role === 'viewer';
  const isPlatformAdmin = user?.is_platform_admin === true;
  const canEdit = !isViewer;
  const canApprove = isAdmin || isFinance;
  const canManageUsers = isAdmin;
  const canViewAudit = isAdmin || isAccountant;

  const value = {
    user,
    company,
    currency,
    loading,
    login,
    register,
    logout,
    refreshCompany,
    isAuthenticated: !!user,
    
    // View mode
    viewMode,
    setViewMode,
    VIEW_MODES,
    
    // Role helpers
    isAdmin,
    isFinance,
    isAccountant,
    isViewer,
    isPlatformAdmin,
    canEdit,
    canApprove,
    canManageUsers,
    canViewAudit
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
